/**
 * Settings drawer controls for the globe: toggles, sliders, canvas
 * background (solid/gradient) state, and color/light inputs. Extracted
 * verbatim from the client monolith (Phase 2, PR-C). Mutates the live globe
 * via the typed window.globe (GlobeApi) seam; handlers run after the globe is
 * constructed.
 */
/* eslint-disable */
import { colors } from '../../../views/colors'

export function initGlobeSettings(): void {
    // Settings toggles
    document.querySelectorAll('.settings-toggle').forEach(function(toggle) {
      // Keyboard operability for the role="switch" toggles (Enter/Space).
      toggle.addEventListener('keydown', function(e) {
        var key = (e as KeyboardEvent).key;
        if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
          e.preventDefault();
          (toggle as HTMLElement).click();
        }
      });

      toggle.addEventListener('click', function() {
        toggle.classList.toggle('active');
        var setting = toggle.getAttribute('data-setting');
        var isActive = toggle.classList.contains('active');
        toggle.setAttribute('aria-checked', isActive ? 'true' : 'false');

        if (!window.globe) return;

        // Handle different settings
        switch(setting) {
          case 'auto-rotate':
            var rotationSpeedSlider = document.getElementById('rotation-speed') as HTMLInputElement;
            window.globe.spinSpeed = isActive ? parseFloat(rotationSpeedSlider.value) : 0;
            break;
          case 'grid-lines':
            if (window.globe.meshes.gridLines) {
              window.globe.meshes.gridLines.visible = isActive;
              window.globe.needsRender = true;
            }
            break;
          case 'country-borders':
            if (window.globe.meshes.countryBorders) {
              window.globe.meshes.countryBorders.forEach(mesh => {
                mesh.visible = isActive;
              });
              window.globe.needsRender = true;
            }
            break;
          case 'state-borders':
            if (window.globe.meshes.stateBorders) {
              window.globe.meshes.stateBorders.forEach(mesh => {
                mesh.visible = isActive;
              });
              window.globe.needsRender = true;
            }
            break;
          case 'dots':
            if (window.globe.meshes.dots) {
              window.globe.meshes.dots.visible = isActive;
              window.globe.needsRender = true;
            }
            break;
          case 'topography':
            if (window.globe && window.globe.meshes.globeFill) {
              window.globe.topographyEnabled = isActive;
              if (isActive && window.globe.bumpTexture) {
                var intensitySlider = document.getElementById('topography-intensity') as HTMLInputElement | null;
                var bumpScale = intensitySlider ? parseFloat(intensitySlider.value) : 0.5;
                window.globe.meshes.globeFill.material.bumpMap = window.globe.bumpTexture;
                window.globe.meshes.globeFill.material.bumpScale = bumpScale;
                // Increase shininess for more visible terrain highlights
                window.globe.meshes.globeFill.material.shininess = 40;
              } else {
                // Bump map is loaded lazily on first enable (deferred off the
                // initial-load path); loadBumpTexture applies it once ready.
                if (isActive && window.globe.loadBumpTexture) {
                  window.globe.loadBumpTexture();
                }
                window.globe.meshes.globeFill.material.bumpMap = null;
                // Restore original shininess
                window.globe.meshes.globeFill.material.shininess = 10;
              }
              window.globe.meshes.globeFill.material.needsUpdate = true;
              window.globe.needsRender = true;
            }
            break;
          case 'canvas-gradient':
            if (window.canvasGradientState) {
              window.canvasGradientState.toggle(isActive);
            }
            break;
          case 'key-light-orbit':
            if (window.globe) {
              // Restart the sweep from the resting position each time the orbit
              // is enabled so it never jumps to a stale angle.
              if (isActive) window.globe.keyLightOrbitAngle = 0;
              window.globe.keyLightOrbit = isActive;
              window.globe.needsRender = true;
            }
            break;
        }
      });
    });

    // Country Mode toggle

    // Rotation speed slider
    var rotationSpeedInput = document.getElementById('rotation-speed');
    var rotationSpeedValue = document.getElementById('rotation-speed-value');
    if (rotationSpeedInput) {
      rotationSpeedInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        rotationSpeedValue.textContent = value.toFixed(1);
        if (window.globe) {
          var autoRotateToggle = document.querySelector('[data-setting="auto-rotate"]');
          if (autoRotateToggle && autoRotateToggle.classList.contains('active')) {
            window.globe.spinSpeed = value;
          }
        }
      });
    }

    // Drag inertia slider
    var dragInertiaInput = document.getElementById('drag-inertia');
    var dragInertiaValue = document.getElementById('drag-inertia-value');
    if (dragInertiaInput) {
      dragInertiaInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        dragInertiaValue.textContent = value.toFixed(2);
        if (window.globe) {
          window.globe.inertiaDecay = value;
        }
      });
    }

    // Globe opacity
    var globeOpacityInput = document.getElementById('globe-opacity');
    var globeOpacityValue = document.getElementById('globe-opacity-value');
    if (globeOpacityInput) {
      globeOpacityInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        globeOpacityValue.textContent = value.toFixed(2);
        if (window.globe && window.globe.meshes.globeFill) {
          window.globe.meshes.globeFill.material.opacity = value;
          window.globe.needsRender = true;
        }
      });
    }

    // Zoom level
    var zoomLevelInput = document.getElementById('zoom-level');
    var zoomLevelValue = document.getElementById('zoom-level-value');
    if (zoomLevelInput) {
      zoomLevelInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        zoomLevelValue.textContent = value.toFixed(1);
        if (window.globe) {
          window.globe.zoom = value;
          window.globe.needsRender = true;
        }
      });
    }

    // Canvas background state
    var isGradient = false;
    var gradientTop: string = colors.globe.gradientTop;
    var gradientMiddle: string = colors.globe.gradientMiddle;
    var gradientBottom: string = colors.globe.gradientBottom;
    var gradientMiddlePos = 50;
    var solidColor: string = colors.white;

    function updateCanvasBackground() {
      var wrapper = document.querySelector('.globe-wrapper') as HTMLElement | null;
      if (!wrapper) return;
      
      if (isGradient) {
        wrapper.style.background = 'linear-gradient(to bottom, ' + gradientTop + ' 0%, ' + gradientMiddle + ' ' + gradientMiddlePos + '%, ' + gradientBottom + ' 100%)';
      } else {
        wrapper.style.background = solidColor;
      }
    }

    // Canvas gradient toggle - handled in the settings toggle switch section above
    var gradientOptionsEl = document.getElementById('canvas-gradient-options');
    var solidOptionsEl = document.getElementById('canvas-solid-options');

    // Solid color input
    var solidColorInput = document.getElementById('color-canvas-solid');
    var solidColorValue = document.getElementById('color-canvas-solid-value');
    if (solidColorInput) {
      solidColorInput.addEventListener('input', function(e) {
        solidColor = (e.target as HTMLInputElement).value;
        solidColorValue.textContent = solidColor;
        updateCanvasBackground();
      });
    }

    // Gradient top color
    var gradientTopInput = document.getElementById('color-gradient-top');
    var gradientTopValue = document.getElementById('color-gradient-top-value');
    if (gradientTopInput) {
      gradientTopInput.addEventListener('input', function(e) {
        gradientTop = (e.target as HTMLInputElement).value;
        gradientTopValue.textContent = gradientTop;
        updateCanvasBackground();
      });
    }

    // Gradient middle color
    var gradientMiddleInput = document.getElementById('color-gradient-middle');
    var gradientMiddleValue = document.getElementById('color-gradient-middle-value');
    if (gradientMiddleInput) {
      gradientMiddleInput.addEventListener('input', function(e) {
        gradientMiddle = (e.target as HTMLInputElement).value;
        gradientMiddleValue.textContent = gradientMiddle;
        updateCanvasBackground();
      });
    }

    // Gradient bottom color
    var gradientBottomInput = document.getElementById('color-gradient-bottom');
    var gradientBottomValue = document.getElementById('color-gradient-bottom-value');
    if (gradientBottomInput) {
      gradientBottomInput.addEventListener('input', function(e) {
        gradientBottom = (e.target as HTMLInputElement).value;
        gradientBottomValue.textContent = gradientBottom;
        updateCanvasBackground();
      });
    }

    // Gradient middle position
    var gradientMiddlePosInput = document.getElementById('gradient-middle-pos');
    var gradientMiddlePosValue = document.getElementById('gradient-middle-pos-value');
    if (gradientMiddlePosInput) {
      gradientMiddlePosInput.addEventListener('input', function(e) {
        gradientMiddlePos = parseInt((e.target as HTMLInputElement).value);
        gradientMiddlePosValue.textContent = gradientMiddlePos + '%';
        updateCanvasBackground();
      });
    }

    // Handle canvas-gradient toggle in the existing toggle handler
    window.canvasGradientState = {
      isGradient: false,
      toggle: function(active) {
        isGradient = active;
        gradientOptionsEl.style.display = active ? 'block' : 'none';
        solidOptionsEl.style.display = active ? 'none' : 'block';
        updateCanvasBackground();
      }
    };

    // Globe fill color
    var globeFillColorInput = document.getElementById('color-globe-fill');
    var globeFillColorValue = document.getElementById('color-globe-fill-value');
    if (globeFillColorInput) {
      globeFillColorInput.addEventListener('input', function(e) {
        var color = (e.target as HTMLInputElement).value;
        globeFillColorValue.textContent = color;
        if (window.globe && window.globe.meshes.globeFill) {
          window.globe.meshes.globeFill.material.color.setStyle(color);
          window.globe.needsRender = true;
        }
      });
    }

    // Country border color
    var countryBorderColorInput = document.getElementById('color-country-border');
    var countryBorderColorValue = document.getElementById('color-country-border-value');
    if (countryBorderColorInput) {
      countryBorderColorInput.addEventListener('input', function(e) {
        var color = (e.target as HTMLInputElement).value;
        countryBorderColorValue.textContent = color;
        if (window.globe && window.globe.meshes.countryBorders) {
          window.globe.meshes.countryBorders.forEach(mesh => {
            mesh.material.color.setStyle(color);
            mesh.userData.originalColor = parseInt(color.replace('#', '0x'));
          });
          window.globe.needsRender = true;
        }
      });
    }

    // State border color
    var stateBorderColorInput = document.getElementById('color-state-border');
    var stateBorderColorValue = document.getElementById('color-state-border-value');
    if (stateBorderColorInput) {
      stateBorderColorInput.addEventListener('input', function(e) {
        var color = (e.target as HTMLInputElement).value;
        stateBorderColorValue.textContent = color;
        if (window.globe && window.globe.meshes.stateBorders) {
          window.globe.meshes.stateBorders.forEach(mesh => {
            mesh.material.color.setStyle(color);
            mesh.userData.originalColor = parseInt(color.replace('#', '0x'));
          });
          window.globe.needsRender = true;
        }
      });
    }

    // Dots color
    var dotsColorInput = document.getElementById('color-dots');
    var dotsColorValue = document.getElementById('color-dots-value');
    if (dotsColorInput) {
      dotsColorInput.addEventListener('input', function(e) {
        var color = (e.target as HTMLInputElement).value;
        dotsColorValue.textContent = color;
        if (window.globe && window.globe.meshes.dots) {
          window.globe.meshes.dots.material.uniforms.color.value.setStyle(color);
          window.globe.needsRender = true;
        }
      });
    }

    // Dot size
    var dotSizeInput = document.getElementById('dot-size');
    var dotSizeValue = document.getElementById('dot-size-value');
    if (dotSizeInput) {
      dotSizeInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        dotSizeValue.textContent = value.toFixed(1);
        if (window.globe && window.globe.meshes.dots) {
          window.globe.meshes.dots.material.uniforms.size.value = value;
          window.globe.needsRender = true;
        }
      });
    }

    // Region fill color
    var regionFillColorInput = document.getElementById('color-region-fill');
    var regionFillColorValue = document.getElementById('color-region-fill-value');
    if (regionFillColorInput) {
      regionFillColorInput.addEventListener('input', function(e) {
        var color = (e.target as HTMLInputElement).value;
        regionFillColorValue.textContent = color;
        if (window.globe) {
          var colorHex = parseInt(color.replace('#', '0x'));
          window.globe.HOVER_FILL_COLOR = colorHex;
          // Update currently visible fills
          if (window.globe.selectedMesh && window.globe.selectedMesh.userData.fillMesh) {
            window.globe.selectedMesh.userData.fillMesh.material.color.setHex(colorHex);
            window.globe.selectedMesh.userData.fillMesh.material.needsUpdate = true;
          }
          if (window.globe.hoveredMesh && window.globe.hoveredMesh.userData.fillMesh && window.globe.hoveredMesh !== window.globe.selectedMesh) {
            window.globe.hoveredMesh.userData.fillMesh.material.color.setHex(colorHex);
            window.globe.hoveredMesh.userData.fillMesh.material.needsUpdate = true;
          }
          window.globe.needsRender = true;
        }
      });
    }

    // Region fill opacity
    var regionFillOpacityInput = document.getElementById('region-fill-opacity');
    var regionFillOpacityValue = document.getElementById('region-fill-opacity-value');
    if (regionFillOpacityInput) {
      regionFillOpacityInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        regionFillOpacityValue.textContent = value.toFixed(2);
        if (window.globe) {
          window.globe.HOVER_FILL_OPACITY = value;
          // Update currently visible fills
          if (window.globe.selectedMesh && window.globe.selectedMesh.userData.fillMesh) {
            window.globe.selectedMesh.userData.fillMesh.material.opacity = value;
            window.globe.selectedMesh.userData.fillMesh.material.needsUpdate = true;
          }
          if (window.globe.hoveredMesh && window.globe.hoveredMesh.userData.fillMesh && window.globe.hoveredMesh !== window.globe.selectedMesh) {
            window.globe.hoveredMesh.userData.fillMesh.material.opacity = value;
            window.globe.hoveredMesh.userData.fillMesh.material.needsUpdate = true;
          }
          window.globe.needsRender = true;
        }
      });
    }

    // Topography intensity
    var topographyIntensityInput = document.getElementById('topography-intensity');
    var topographyIntensityValue = document.getElementById('topography-intensity-value');
    if (topographyIntensityInput) {
      topographyIntensityInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        topographyIntensityValue.textContent = value.toFixed(2);
        if (window.globe && window.globe.meshes.globeFill && window.globe.topographyEnabled) {
          window.globe.meshes.globeFill.material.bumpScale = value;
          window.globe.meshes.globeFill.material.needsUpdate = true;
          window.globe.needsRender = true;
        }
      });
    }

    // Key light orbit speed
    var orbitSpeedInput = document.getElementById('key-light-orbit-speed');
    var orbitSpeedValue = document.getElementById('key-light-orbit-speed-value');
    if (orbitSpeedInput) {
      orbitSpeedInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        orbitSpeedValue.textContent = value.toFixed(1);
        if (window.globe) {
          window.globe.keyLightOrbitSpeed = value;
        }
      });
    }

    // Move the key light to its resting position immediately so slider changes
    // apply right away. While the orbit is running the animation loop derives
    // the swept position from these same values, so the two stay consistent.
    function applyKeyLightPosition() {
      if (!window.globe || !window.globe.meshes.keyLight) return;
      var keyLight = window.globe.meshes.keyLight;
      keyLight.position.set(window.globe.keyLightX, window.globe.keyLightY, window.globe.keyLightZ);
      if (window.globe.meshes.dots && window.globe.meshes.dots.material.uniforms) {
        window.globe.meshes.dots.material.uniforms.lightPosition.value.copy(keyLight.position);
      }
      window.globe.needsRender = true;
    }

    // Key light position X
    var keyLightXInput = document.getElementById('key-light-x');
    var keyLightXValue = document.getElementById('key-light-x-value');
    if (keyLightXInput) {
      keyLightXInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        keyLightXValue.textContent = value.toFixed(1);
        if (window.globe) {
          window.globe.keyLightX = value;
          applyKeyLightPosition();
        }
      });
    }

    // Key light position Y
    var keyLightYInput = document.getElementById('key-light-y');
    var keyLightYValue = document.getElementById('key-light-y-value');
    if (keyLightYInput) {
      keyLightYInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        keyLightYValue.textContent = value.toFixed(1);
        if (window.globe) {
          window.globe.keyLightY = value;
          applyKeyLightPosition();
        }
      });
    }

    // Key light position Z
    var keyLightZInput = document.getElementById('key-light-z');
    var keyLightZValue = document.getElementById('key-light-z-value');
    if (keyLightZInput) {
      keyLightZInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        keyLightZValue.textContent = value.toFixed(1);
        if (window.globe) {
          window.globe.keyLightZ = value;
          applyKeyLightPosition();
        }
      });
    }

    // Key light intensity
    var keyLightIntensityInput = document.getElementById('key-light-intensity');
    var keyLightIntensityValue = document.getElementById('key-light-intensity-value');
    if (keyLightIntensityInput) {
      keyLightIntensityInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        keyLightIntensityValue.textContent = value.toFixed(1);
        if (window.globe && window.globe.meshes.keyLight) {
          window.globe.meshes.keyLight.intensity = value;
          window.globe.needsRender = true;
        }
      });
    }

    // Key light color
    var keyLightColorInput = document.getElementById('key-light-color');
    var keyLightColorValue = document.getElementById('key-light-color-value');
    if (keyLightColorInput) {
      keyLightColorInput.addEventListener('input', function(e) {
        var color = (e.target as HTMLInputElement).value;
        keyLightColorValue.textContent = color;
        if (window.globe && window.globe.meshes.keyLight) {
          window.globe.meshes.keyLight.color.setStyle(color);
          window.globe.needsRender = true;
        }
      });
    }

    // Fill light intensity
    var fillLightIntensityInput = document.getElementById('fill-light-intensity');
    var fillLightIntensityValue = document.getElementById('fill-light-intensity-value');
    if (fillLightIntensityInput) {
      fillLightIntensityInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        fillLightIntensityValue.textContent = value.toFixed(1);
        if (window.globe && window.globe.meshes.fillLight) {
          window.globe.meshes.fillLight.intensity = value;
          window.globe.needsRender = true;
        }
      });
    }

    // Fill light color
    var fillLightColorInput = document.getElementById('fill-light-color');
    var fillLightColorValue = document.getElementById('fill-light-color-value');
    if (fillLightColorInput) {
      fillLightColorInput.addEventListener('input', function(e) {
        var color = (e.target as HTMLInputElement).value;
        fillLightColorValue.textContent = color;
        if (window.globe && window.globe.meshes.fillLight) {
          window.globe.meshes.fillLight.color.setStyle(color);
          window.globe.needsRender = true;
        }
      });
    }

    // Rim light intensity
    var rimLightIntensityInput = document.getElementById('rim-light-intensity');
    var rimLightIntensityValue = document.getElementById('rim-light-intensity-value');
    if (rimLightIntensityInput) {
      rimLightIntensityInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        rimLightIntensityValue.textContent = value.toFixed(1);
        if (window.globe && window.globe.meshes.rimLight) {
          window.globe.meshes.rimLight.intensity = value;
          window.globe.needsRender = true;
        }
      });
    }

    // Rim light color
    var rimLightColorInput = document.getElementById('rim-light-color');
    var rimLightColorValue = document.getElementById('rim-light-color-value');
    if (rimLightColorInput) {
      rimLightColorInput.addEventListener('input', function(e) {
        var color = (e.target as HTMLInputElement).value;
        rimLightColorValue.textContent = color;
        if (window.globe && window.globe.meshes.rimLight) {
          window.globe.meshes.rimLight.color.setStyle(color);
          window.globe.needsRender = true;
        }
      });
    }

    // Ambient light intensity
    var ambientLightIntensityInput = document.getElementById('ambient-light-intensity');
    var ambientLightIntensityValue = document.getElementById('ambient-light-intensity-value');
    if (ambientLightIntensityInput) {
      ambientLightIntensityInput.addEventListener('input', function(e) {
        var value = parseFloat((e.target as HTMLInputElement).value);
        ambientLightIntensityValue.textContent = value.toFixed(1);
        if (window.globe && window.globe.meshes.ambientLight) {
          window.globe.meshes.ambientLight.intensity = value;
          window.globe.needsRender = true;
        }
      });
    }
}
